import React, { useState, useEffect } from 'react';
import { clientsApi, operatorPermissionsApi } from '../services/api';
import { User } from '../contexts/AuthContext';
import ClientFormComplete from './ClientFormComplete';

interface Client {
  id: number;
  nombre: string;
  email: string;
  telefono: string;
  direccion: string;
  rut?: string;
  contactoPrincipal: string;
  observaciones?: string;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Operator {
  id: number;
  name: string;
  email: string;
  username: string;
  role: string;
}

interface BlockedOperator {
  id: number;
  operatorId: number;
  clientId: number;
  blockedReason?: string;
  blockedBy: number;
  createdAt: string;
  operator: Operator;
  blockedByUser: {
    id: number;
    name: string;
    email: string;
  };
}

interface AssignedOperator {
  id: number;
  operatorId: number;
  clientId: number;
  assignedBy: number;
  createdAt: string;
  operator: Operator;
  assignedByUser: {
    id: number;
    name: string;
    email: string;
  };
}

interface ClientManagementProps {
  user: User;
}

const ClientManagement: React.FC<ClientManagementProps> = ({ user }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para modal de permisos de operadores (unificado)
  const [showOperatorsModal, setShowOperatorsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [availableOperators, setAvailableOperators] = useState<Operator[]>([]);
  const [blockedOperators, setBlockedOperators] = useState<BlockedOperator[]>([]);
  const [assignedOperators, setAssignedOperators] = useState<AssignedOperator[]>([]);
  const [blockReason, setBlockReason] = useState('');
  const [operatorsLoading, setOperatorsLoading] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getAll();
      if (response.data.success) {
        setClients(response.data.data);
      }
    } catch (error: any) {
      console.error('Error cargando clientes:', error);
      setError('Error cargando clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowForm(true);
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleDeleteClient = async (clientId: number) => {
    if (!confirm('¿Está seguro de eliminar este cliente?')) return;
    
    try {
      const response = await clientsApi.delete(clientId);
      if (response.data.success) {
        setSuccess('Cliente eliminado exitosamente');
        loadClients();
      }
    } catch (error: any) {
      console.error('Error eliminando cliente:', error);
      setError('Error eliminando cliente');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingClient(null);
    setSuccess(editingClient ? 'Cliente actualizado exitosamente' : 'Cliente creado exitosamente');
    loadClients();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingClient(null);
    setShowForm(false);
  };

  // === FUNCIONES PARA GESTIÓN DE OPERADORES BLOQUEADOS ===

  const handleManageOperators = async (client: Client) => {
    setSelectedClient(client);
    setShowOperatorsModal(true);
    setOperatorsLoading(true);

    try {
      // Cargar operadores disponibles, bloqueados y asignados en paralelo
      const [availableResponse, blockedResponse, assignedResponse] = await Promise.all([
        operatorPermissionsApi.getAvailableOperators(client.id),
        operatorPermissionsApi.getBlockedOperators(client.id),
        operatorPermissionsApi.getAssignedOperators(client.id)
      ]);

      if (availableResponse.data.success) {
        setAvailableOperators(availableResponse.data.data);
      }

      if (blockedResponse.data.success) {
        setBlockedOperators(blockedResponse.data.data);
      }

      if (assignedResponse.data.success) {
        setAssignedOperators(assignedResponse.data.data);
      }
    } catch (error: any) {
      console.error('Error cargando operadores:', error);
      setError('Error cargando operadores: ' + (error.response?.data?.error || error.message));
    } finally {
      setOperatorsLoading(false);
    }
  };

  const handleBlockOperator = async (operatorId: number) => {
    if (!selectedClient) return;

    try {
      const response = await operatorPermissionsApi.blockOperator(
        selectedClient.id,
        operatorId,
        blockReason.trim() || undefined
      );

      if (response.data.success) {
        setSuccess('Operador bloqueado exitosamente');
        setBlockReason('');
        // Recargar listas
        await handleManageOperators(selectedClient);
      }
    } catch (error: any) {
      console.error('Error bloqueando operador:', error);
      setError('Error bloqueando operador: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUnblockOperator = async (operatorId: number) => {
    if (!selectedClient) return;

    try {
      const response = await operatorPermissionsApi.unblockOperator(
        selectedClient.id,
        operatorId
      );

      if (response.data.success) {
        setSuccess('Operador desbloqueado exitosamente');
        // Recargar listas
        await handleManageOperators(selectedClient);
      }
    } catch (error: any) {
      console.error('Error desbloqueando operador:', error);
      setError('Error desbloqueando operador: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAssignOperator = async (operatorId: number) => {
    if (!selectedClient) return;

    console.log('🔧 DEBUG - Asignando operador:', {
      operatorId,
      clientId: selectedClient.id,
      clientName: selectedClient.nombre
    });

    try {
      const response = await operatorPermissionsApi.assignClient(
        operatorId,
        selectedClient.id
      );

      console.log('🔧 DEBUG - Respuesta del servidor:', response.data);

      if (response.data.success) {
        setSuccess('Operador asignado exitosamente');
        console.log('🔧 DEBUG - Asignación exitosa, recargando listas...');
        // Recargar listas
        await handleManageOperators(selectedClient);
      } else {
        console.log('🔧 DEBUG - La respuesta no fue exitosa:', response.data);
        setError('Error: ' + (response.data.error || 'Respuesta inesperada del servidor'));
      }
    } catch (error: any) {
      console.error('🔧 DEBUG - Error asignando operador:', error);
      console.error('🔧 DEBUG - Error response:', error.response?.data);
      setError('Error asignando operador: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUnassignOperator = async (operatorId: number) => {
    if (!selectedClient) return;

    try {
      const response = await operatorPermissionsApi.unassignClient(
        operatorId,
        selectedClient.id
      );

      if (response.data.success) {
        setSuccess('Operador desasignado exitosamente');
        // Recargar listas
        await handleManageOperators(selectedClient);
      }
    } catch (error: any) {
      console.error('Error desasignando operador:', error);
      setError('Error desasignando operador: ' + (error.response?.data?.error || error.message));
    }
  };

  const closeOperatorsModal = () => {
    setShowOperatorsModal(false);
    setSelectedClient(null);
    setAvailableOperators([]);
    setBlockedOperators([]);
    setAssignedOperators([]);
    setBlockReason('');
  };

  // Mostrar formulario si está en modo crear/editar
  if (showForm) {
    return (
      <ClientFormComplete 
        user={user}
        clientToEdit={editingClient}
        isEditing={!!editingClient}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  // Mostrar loading
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="spinner" />
        <span style={{ marginLeft: '0.5rem' }}>Cargando clientes...</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
          🏢 Gestión de Clientes
        </h2>
        <button 
          onClick={handleCreateClient}
          className="btn btn-primary"
        >
          + Crear Cliente
        </button>
      </div>

      {error && (
        <div className="error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="success" style={{ marginBottom: '1rem' }}>
          {success}
        </div>
      )}

      {/* Buscador */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="🔍 Buscar por nombre, contacto, email o RUT..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
      </div>

      {/* SOLO Lista de clientes */}
      <div className="card">
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
          Clientes Registrados ({clients.filter(client => {
            const search = searchTerm.toLowerCase();
            return (
              client.nombre.toLowerCase().includes(search) ||
              client.contactoPrincipal.toLowerCase().includes(search) ||
              client.email.toLowerCase().includes(search) ||
              (client.rut && client.rut.toLowerCase().includes(search))
            );
          }).length})
        </h3>

        {clients.filter(client => {
          const search = searchTerm.toLowerCase();
          return (
            client.nombre.toLowerCase().includes(search) ||
            client.contactoPrincipal.toLowerCase().includes(search) ||
            client.email.toLowerCase().includes(search) ||
            (client.rut && client.rut.toLowerCase().includes(search))
          );
        }).length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem', 
            color: '#6b7280' 
          }}>
            <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              No hay clientes registrados
            </p>
            <p>Crea tu primer cliente para comenzar a gestionar auditorías</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '0.875rem'
            }}>
              <thead>
                <tr style={{ 
                  borderBottom: '2px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Nombre</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Teléfono</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Contacto</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Estado</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clients.filter(client => {
                  const search = searchTerm.toLowerCase();
                  return (
                    client.nombre.toLowerCase().includes(search) ||
                    client.contactoPrincipal.toLowerCase().includes(search) ||
                    client.email.toLowerCase().includes(search) ||
                    (client.rut && client.rut.toLowerCase().includes(search))
                  );
                }).map((client) => (
                  <tr key={client.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{client.nombre}</div>
                        {client.rut && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            RUT: {client.rut}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>{client.email}</td>
                    <td style={{ padding: '0.75rem' }}>{client.telefono}</td>
                    <td style={{ padding: '0.75rem' }}>{client.contactoPrincipal}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: client.activo ? '#4CAF5020' : '#ff980020',
                        color: client.activo ? '#4CAF50' : '#ff9800',
                        border: `1px solid ${client.activo ? '#4CAF50' : '#ff9800'}`
                      }}>
                        {client.activo ? '✅ Activo' : '⏸️ Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEditClient(client)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            marginRight: '0.5rem'
                          }}
                        >
                          Editar
                        </button>
                        {user.role === 'admin' && (
                          <button
                            onClick={() => handleDeleteClient(client.id)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              marginRight: '0.5rem'
                            }}
                          >
                            Eliminar
                          </button>
                        )}
                        <button
                          onClick={() => handleManageOperators(client)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            marginLeft: '0.5rem'
                          }}
                        >
                          Operadores
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal para gestión de operadores bloqueados */}
      {showOperatorsModal && selectedClient && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>
                🎛️ Centro de Permisos - {selectedClient.nombre}
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                Gestiona todos los permisos de operadores para este cliente en un solo lugar:
                <br />
                <strong>• Asignar:</strong> Operadores que SOLO pueden trabajar con este cliente
                <br />
                <strong>• Bloquear:</strong> Operadores que NO pueden trabajar con este cliente
              </p>
            </div>

            {operatorsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div>Cargando operadores...</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Lista de operadores disponibles para bloquear */}
                <div>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
                    Operadores Disponibles ({availableOperators.length})
                  </h4>
                  {availableOperators.length === 0 ? (
                    <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                      Todos los operadores están bloqueados para este cliente
                    </p>
                  ) : (
                    <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {availableOperators.map((operator) => (
                        <div key={operator.id} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          backgroundColor: '#f9fafb'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontWeight: '500' }}>{operator.name}</div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {operator.email} • {operator.role}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => handleAssignOperator(operator.id)}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: '#8b5cf6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer'
                                }}
                              >
                                ✅ Asignar
                              </button>
                              <button
                                onClick={() => handleBlockOperator(operator.id)}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer'
                                }}
                              >
                                🚫 Bloquear
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Campo para razón del bloqueo */}
                  {availableOperators.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                        Razón del bloqueo (opcional):
                      </label>
                      <textarea
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="Ej: Ex-empleado de la empresa"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          resize: 'vertical',
                          minHeight: '60px'
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Lista de operadores bloqueados */}
                <div>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
                    Operadores Bloqueados ({blockedOperators.length})
                  </h4>
                  {blockedOperators.length === 0 ? (
                    <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                      No hay operadores bloqueados para este cliente
                    </p>
                  ) : (
                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                      {blockedOperators.map((blocked) => (
                        <div key={blocked.id} style={{
                          border: '1px solid #fecaca',
                          borderRadius: '0.375rem',
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          backgroundColor: '#fef2f2'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '500' }}>{blocked.operator.name}</div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                                {blocked.operator.email} • {blocked.operator.role}
                              </div>
                              {blocked.blockedReason && (
                                <div style={{ fontSize: '0.75rem', color: '#991b1b', marginBottom: '0.25rem' }}>
                                  Razón: {blocked.blockedReason}
                                </div>
                              )}
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Bloqueado por: {blocked.blockedByUser?.name || 'N/A'}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Fecha: {new Date(blocked.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnblockOperator(blocked.operatorId)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                marginLeft: '0.5rem'
                              }}
                            >
                              Desbloquear
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* === SECCIÓN DE OPERADORES ASIGNADOS === */}
                <div>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
                    ✅ Operadores Asignados ({assignedOperators.length})
                  </h4>
                  {assignedOperators.length === 0 ? (
                    <p style={{
                      color: '#6b7280',
                      fontStyle: 'italic',
                      backgroundColor: '#f9fafb',
                      padding: '1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      No hay operadores asignados específicamente a este cliente.
                      <br />
                      <small>Esto significa que cualquier operador disponible puede tomar auditorías de este cliente.</small>
                    </p>
                  ) : (
                    <div style={{ marginBottom: '1rem' }}>
                      {assignedOperators.map((assigned) => (
                        <div
                          key={assigned.id}
                          style={{
                            padding: '0.75rem',
                            backgroundColor: '#ecfdf5',
                            border: '1px solid #10b981',
                            borderRadius: '0.5rem',
                            marginBottom: '0.5rem'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 'bold', color: '#065f46' }}>
                                {assigned.operator.name} ({assigned.operator.username})
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#059669' }}>
                                {assigned.operator.email} - {assigned.operator.role}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                Asignado: {new Date(assigned.createdAt).toLocaleDateString()}
                                {assigned.assignedByUser && ` por ${assigned.assignedByUser.name}`}
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnassignOperator(assigned.operatorId)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                marginLeft: '0.5rem'
                              }}
                            >
                              Desasignar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Botones de acción */}
            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={closeOperatorsModal}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;